import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, KeyRound, UserRound } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Badge, Button, Field, Input, PageHeader } from "../components/ui";
import { api } from "../lib/api";
import { useAuth, type User } from "../lib/auth-context";
import styles from "./account.module.css";

type Account = User & { id: number };

export function AccountPage() {
  const query = useQuery({
    queryKey: ["account"],
    queryFn: () => api<Account>("/api/account"),
  });
  return (
    <main className={styles.page}>
      <PageHeader
        title="Профиль"
        description="Учётная запись общая для DEV и PROD. Изменения имени и пароля действуют в обеих админках."
      />
      {query.data ? (
        <AccountForm
          key={`${query.data.id}:${query.data.display_name || ""}`}
          account={query.data}
        />
      ) : (
        <div className={styles.loading}>
          {query.isError ? "Не удалось загрузить профиль" : "Загрузка профиля"}
        </div>
      )}
    </main>
  );
}

function AccountForm({ account }: { account: Account }) {
  const { updateUser } = useAuth();
  const [displayName, setDisplayName] = useState(account.display_name || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      api<Account>("/api/account", {
        method: "PUT",
        body: JSON.stringify({
          display_name: displayName || null,
          current_password: currentPassword || null,
          new_password: newPassword || null,
        }),
      }),
    onSuccess: (account) => {
      updateUser(account);
      setCurrentPassword("");
      setNewPassword("");
      setError("");
      setNotice("Изменения сохранены");
      window.setTimeout(() => setNotice(""), 3000);
    },
    onError: (caught) => {
      setNotice("");
      setError(
        caught instanceof Error
          ? caught.message
          : "Не удалось сохранить изменения",
      );
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    mutation.mutate();
  }

  return (
    <form className={styles.layout} onSubmit={submit}>
      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <span>
            <UserRound size={17} />
          </span>
          <div>
            <h2>Учётная запись</h2>
            <p>Имя отображается в интерфейсе и истории действий.</p>
          </div>
        </div>
        <div className={styles.cardBody}>
          <Field label="Логин">
            <Input value={account.username} disabled />
          </Field>
          <Field label="Отображаемое имя">
            <Input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </Field>
          <div className={styles.meta}>
            <span>Роль</span>
            <Badge>
              {account.role === "admin" ? "Администратор" : "Пользователь"}
            </Badge>
          </div>
        </div>
      </section>
      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <span>
            <KeyRound size={17} />
          </span>
          <div>
            <h2>Смена пароля</h2>
            <p>Заполните оба поля только при необходимости сменить пароль.</p>
          </div>
        </div>
        <div className={styles.cardBody}>
          <Field label="Текущий пароль">
            <Input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </Field>
          <Field label="Новый пароль">
            <Input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </Field>
        </div>
      </section>
      <div className={styles.footer}>
        {error ? (
          <p className={styles.error}>{error}</p>
        ) : notice ? (
          <p className={styles.notice}>
            <Check size={14} />
            {notice}
          </p>
        ) : (
          <span />
        )}
        <Button variant="primary" type="submit" loading={mutation.isPending}>
          Сохранить
        </Button>
      </div>
    </form>
  );
}
